import { NgOptimizedImage } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { form, FormField } from '@angular/forms/signals';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { finalize } from 'rxjs';
import { AuthService } from '../../../../../core/services/auth/auth.service';
import { LucideModule } from '../../../../../shared/modules/lucide.module';
import { mensagemDeErroLogin } from '../../../../../shared/utils/mensagem-erro-login';
import { NormalizeService } from '../../../../../shared/utils/normalize.service';
import { EstadoCanal } from '../../services/ouvidoria-publica.service';

interface SecaoManifestacao {
  id: string;
  titulo: string;
  icone: string;
  conteudo: string;
}

const SECOES: readonly SecaoManifestacao[] = [
  { id: 'o-que-e', titulo: 'O que é o serviço?', icone: 'circle-help', conteudo: '' },
  {
    id: 'solicitacoes',
    titulo: 'Solicitações',
    icone: 'clipboard-list',
    conteudo:
      'A manifestação pode ser registrada pelo portal, presencialmente nos postos de atendimento ou ' +
      'pela central telefônica 125. Para o registro on-line informe o CPF, entre com o Login Gov ou ' +
      'siga de forma anônima.',
  },
  {
    id: 'prazo',
    titulo: 'Prazo de conclusão',
    icone: 'clock',
    // Texto de fallback: com o canal carregado, o prazo real do órgão entra no lugar.
    conteudo:
      'A resposta é enviada dentro do prazo publicado pela Ouvidoria do órgão, prorrogável mediante ' +
      'justificativa, conforme a Lei nº 13.460/2017.',
  },
  {
    id: 'formulario',
    titulo: 'Lista/formulário de serviço',
    icone: 'file-text',
    conteudo:
      'Tenha em mãos os dados do ocorrido (o que aconteceu, quando, onde e quem foi o responsável) e, ' +
      'se possível, documentos, fotos ou vídeos que ajudem na análise da manifestação.',
  },
];

@Component({
  selector: 'app-etapa-informacoes-ouvidoria',
  templateUrl: './etapa-informacoes-ouvidoria.component.html',
  styleUrls: ['./etapa-informacoes-ouvidoria.component.css'],
  imports: [FormField, LucideModule, NgOptimizedImage, NgxMaskDirective],
  // Mantém o valor mascarado no modelo; o envio normaliza os dígitos antes de chamar a API.
  providers: [provideNgxMask({ dropSpecialCharacters: false })],
})
export class EtapaInformacoesOuvidoriaComponent {
  private readonly authService = inject(AuthService);
  private readonly normalizeService = inject(NormalizeService);
  private readonly destroyRef = inject(DestroyRef);

  readonly titulo = input('Manifestação');
  readonly icone = input('megaphone');
  readonly descricao = input('');

  /**
   * Indica que o login foi feito pelo formulário desta etapa. Fica no componente pai porque a etapa
   * é recriada a cada troca de passo; com sessão ativa vinda de outra tela continua `false`, de modo
   * que as opções iniciais (login, gov.br e anônimo) seguem disponíveis aqui.
   */
  readonly sessaoIniciada = input(false);

  /** Cada incremento vindo do pai rola até o card de acesso e foca o campo de documento. */
  readonly focarLogin = input(0);

  /** O canal do órgão respondeu 200 e o tipo da rota é enviável: só então dá para liberar. */
  readonly canalDisponivel = input(false);

  /** Sem anonimato habilitado no canal, o botão nem é renderizado. */
  readonly permiteAnonimato = input(false);

  readonly estadoCanal = input<EstadoCanal>('inicial');

  /** "30 dias úteis" — o prazo do canal, que substitui a constante da Lei na seção de prazo. */
  readonly prazoTexto = input('');

  readonly solicitar = output<void>();

  /** Seguir sem se identificar; separado de `solicitar` porque muda o que a etapa pede. */
  readonly seguirAnonimo = output<void>();

  readonly autenticado = output<void>();

  readonly saiu = output<void>();

  /** Refaz o GET do canal — o "Tentar novamente" do aviso. */
  readonly tentarNovamente = output<void>();

  /** Acesso pelo gov.br — ainda sem integração, apenas libera a etapa de solicitação. */
  readonly acessarGov = output<void>();

  private readonly usuario = toSignal(this.authService.currentUser, {
    initialValue: this.authService.currentUserValue,
  });

  readonly logado = computed(() => this.sessaoIniciada() && !!this.usuario());

  readonly nomeUsuario = computed(() => this.usuario()?.nome?.trim() || 'cidadão');

  /** Máscara nativa do ngx-mask: alterna entre CPF e CNPJ conforme os dígitos digitados. */
  readonly mascaraDocumento = 'CPF_CNPJ';

  readonly dados = signal({ documento: '', senha: '' });
  readonly loginForm = form(this.dados);

  readonly senhaVisivel = signal(false);
  readonly entrando = signal(false);
  readonly erroLogin = signal('');
  readonly tentouEntrar = signal(false);

  readonly documentoValido = computed(() => {
    const digitos = this.dados().documento.replace(/\D/g, '');
    return digitos.length === 11 || digitos.length === 14;
  });

  readonly senhaValida = computed(() => this.dados().senha.trim().length > 0);

  readonly loginValido = computed(() => this.documentoValido() && this.senhaValida());

  readonly secoes = SECOES;

  readonly carregandoCanal = computed(
    () => this.estadoCanal() === 'inicial' || this.estadoCanal() === 'carregando',
  );

  /** O aviso só aparece depois que a API respondeu: durante o carregamento não há erro a mostrar. */
  readonly avisoCanal = computed(() => {
    switch (this.estadoCanal()) {
      case 'sem-canal':
        return (
          'A Ouvidoria deste órgão ainda não está recebendo manifestações on-line. Você pode ' +
          'registrar sua manifestação presencialmente ou pela central telefônica.'
        );
      case 'falha':
        return 'Não foi possível consultar a Ouvidoria deste órgão agora. Tente novamente em instantes.';
      default:
        return '';
    }
  });

  /** Só a falha de rede tem o que retentar; "sem canal" é resposta da API, não erro. */
  readonly podeTentarNovamente = computed(() => this.estadoCanal() === 'falha');

  readonly secaoAberta = signal<string>('o-que-e');

  private readonly cartaoLogin = viewChild<ElementRef<HTMLElement>>('cartaoLogin');
  private readonly campoDocumento = viewChild<ElementRef<HTMLInputElement>>('campoDocumento');

  constructor() {
    effect(() => {
      if (this.focarLogin() === 0) return;

      const campo = this.campoDocumento()?.nativeElement;
      const cartao = this.cartaoLogin()?.nativeElement;

      (campo ?? cartao)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      campo?.focus({ preventScroll: true });
    });
  }

  alternarSecao(id: string): void {
    this.secaoAberta.update((aberta) => (aberta === id ? '' : id));
  }

  alternarSenha(): void {
    this.senhaVisivel.update((visivel) => !visivel);
  }

  entrar(evento: Event): void {
    evento.preventDefault();

    this.tentouEntrar.set(true);
    this.erroLogin.set('');

    if (!this.loginValido() || this.entrando()) {
      return;
    }

    const { documento, senha } = this.dados();
    const usuario = this.normalizeService.cpfCnpj(documento);

    this.entrando.set(true);

    this.authService
      .login(usuario, senha)
      .pipe(
        finalize(() => this.entrando.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.dados.update((atual) => ({ ...atual, senha: '' }));
          this.tentouEntrar.set(false);
          this.autenticado.emit();
        },
        error: (erro) => this.erroLogin.set(mensagemDeErroLogin(erro)),
      });
  }

  sair(): void {
    this.authService.logout();
    this.dados.set({ documento: '', senha: '' });
    this.tentouEntrar.set(false);
    this.erroLogin.set('');
    this.senhaVisivel.set(false);
    this.saiu.emit();
  }

  conteudoDaSecao(secao: SecaoManifestacao): string {
    if (secao.id === 'o-que-e') return this.descricao();

    // O prazo publicado é o do CANAL do órgão, não o teto da Lei: é ele que vale para esta
    // manifestação e é ele que o comprovante vai repetir.
    if (secao.id === 'prazo' && this.prazoTexto()) {
      return (
        `A resposta é enviada em até ${this.prazoTexto()}, prorrogáveis mediante justificativa, ` +
        'conforme a Lei nº 13.460/2017.'
      );
    }

    return secao.conteudo;
  }
}
